import { IClaimFilters } from "../../types/claim";

/**
 * The query-string record `GET /claims` receives for a set of list filters.
 * CLE-3035: exported so the claims list can hand the SAME record to the bulk
 * actions as their `filter` target ("all claims matching the current
 * filters") — the server validates it with the same DTO as the list.
 */
export const buildClaimListParams = (
  filters: IClaimFilters
): Record<string, string> => {
  const params: Record<string, string> = {
    accountId: filters.accountId,
  };

  if (filters.search) {
    params.search = filters.search;
  }

  if (filters.startDate) {
    [params.startDate] = filters.startDate.toISOString().split("T");
  }

  if (filters.endDate) {
    [params.endDate] = filters.endDate.toISOString().split("T");
  }

  if (filters.sortField) {
    params.sortField = filters.sortField;
  }

  if (filters.sortOrder) {
    params.sortOrder = filters.sortOrder;
  }

  if (filters.page) {
    params.page = filters.page.toString();
  }

  if (filters.pageSize) {
    params.pageSize = filters.pageSize.toString();
  }

  if (filters.chargeBelowContractRateFilter) {
    params.chargeBelowContractRateFilter =
      filters.chargeBelowContractRateFilter;
  }

  if (filters.effectiveYearMissingFilter) {
    params.effectiveYearMissingFilter = filters.effectiveYearMissingFilter;
  }

  // CLE-2957: claims the pricing engine failed on (no expected amount).
  if (filters.pricingErrorFilter) {
    params.pricingErrorFilter = filters.pricingErrorFilter;
  }

  // CLE-3071: claims priced by revenue code instead of CPT.
  if (filters.revCodeFeeFilter) {
    params.revCodeFeeFilter = filters.revCodeFeeFilter;
  }

  if (filters.minDifferencePercent !== undefined) {
    params.minDifferencePercent = filters.minDifferencePercent.toString();
  }

  if (filters.maxDifferencePercent !== undefined) {
    params.maxDifferencePercent = filters.maxDifferencePercent.toString();
  }

  if (filters.minDifferenceAmount !== undefined) {
    params.minDifferenceAmount = filters.minDifferenceAmount.toString();
  }

  if (filters.categories && filters.categories.length > 0) {
    params.categories = filters.categories.join(",");
  }

  if (filters.underpaymentOnly !== undefined) {
    params.underpaymentOnly = filters.underpaymentOnly.toString();
  }

  if (filters.paymentTypes && filters.paymentTypes.length > 0) {
    params.paymentTypes = filters.paymentTypes.join(",");
  }

  if (filters.denialStatuses && filters.denialStatuses.length > 0) {
    params.denialStatuses = filters.denialStatuses.join(",");
  }

  if (
    filters.denialClassificationCodes &&
    filters.denialClassificationCodes.length > 0
  ) {
    params.denialClassificationCodes =
      filters.denialClassificationCodes.join(",");
  }

  if (filters.renderingTaxonomies && filters.renderingTaxonomies.length > 0) {
    params.renderingTaxonomies = filters.renderingTaxonomies.join(",");
  }

  if (filters.resolutionFilter) {
    params.resolutionFilter = filters.resolutionFilter;
  }

  if (filters.classificationFilter) {
    params.classificationFilter = filters.classificationFilter;
  }

  if (filters.payerIdFilter) {
    params.payerIdFilter = filters.payerIdFilter;
  }

  if (filters.payerIdFilters && filters.payerIdFilters.length > 0) {
    params.payerIdFilters = filters.payerIdFilters.join(",");
  }

  if (filters.patientClassFilter) {
    params.patientClassFilter = filters.patientClassFilter;
  }

  if (filters.claimCategoryFilter) {
    params.claimCategoryFilter = filters.claimCategoryFilter;
  }

  if (filters.hasMatchedOrder) {
    params.hasMatchedOrder = "true";
  }

  if (filters.minEventCount !== undefined) {
    params.minEventCount = String(filters.minEventCount);
  }

  if (filters.maxEventCount !== undefined) {
    params.maxEventCount = String(filters.maxEventCount);
  }

  if (filters.ediConversionFileIds && filters.ediConversionFileIds.length > 0) {
    params.ediConversionFileIds = filters.ediConversionFileIds.join(",");
  }

  return params;
};
